/**
 * Drain the mutation queue. One-at-a-time, in order, so a create always
 * resolves before any of its follow-up updates.
 *
 * Local-ID rewrite: when a create succeeds the server gives us a real
 * eventId. We call queue.rewriteRecordId() so subsequent ops on the same
 * row use the real id, and the event row's PK swaps from local-<uuid> to
 * the real id atomically.
 *
 * "Waiting for create" (shared-docs/offline-first-architecture.md): an
 * update/delete can still carry `local-<uuid>` when its create has not
 * resolved (create in flight, its response lost, or the create dead). The
 * server's id for that create is deterministic — `clientEventId` with the
 * `local-` prefix stripped — so such ops are addressed to the stripped id.
 * Waiting is NOT a failure: the entry is put back to 'pending' WITHOUT
 * counting an attempt, so it can never be dropped as 'dead' (which would
 * silently discard, say, the wake the parent just saved). It is retried as
 * soon as the create lands — later in this drain if one does, otherwise on
 * the next one.
 */

import * as queue from '../db/queue';
import * as eventsRepo from '../db/events';
import { getDB } from '../db/index';
import { rawPost, rawPut, rawDelete } from './network';
import { isOffline } from '../network';

const LOCAL_PREFIX = 'local-';

let inFlight = false;

export function isDraining() {
  return inFlight;
}

function isLocalId(id) {
  return typeof id === 'string' && id.startsWith(LOCAL_PREFIX);
}

/** The id the server uses for this record (local-<uuid> → <uuid>). */
export function serverEventId(id) {
  return isLocalId(id) ? id.slice(LOCAL_PREFIX.length) : id;
}

function isNotFound(err) {
  const status = err?.response?.status ?? err?.status ?? err?.statusCode;
  if (status === 404) return true;
  const msg = String(err?.message || '');
  return /\b404\b/.test(msg) || /not found/i.test(err?.response?.data?.error || '');
}

/**
 * Put an entry back on the queue without counting an attempt.
 *
 * queue.markFailed() increments `attempts` and flips the entry to 'dead' at 8
 * — and 'dead' is never listed again, so the user's edit is gone for good.
 * Waiting for a create is not a failure and must never spend an attempt.
 * (Belongs next to markFailed as queue.markPending(); queue.js is owned by
 * another workstream, so it lives here for now.)
 */
async function requeuePending(id) {
  try {
    await getDB().mutationQueue.update(id, { status: 'pending' });
  } catch {
    /* worst case the entry stays in_flight and the next drain re-lists it */
  }
}

/**
 * Is a create for this record still queued AND retryable? Only 'pending' /
 * 'failed' count: a create stranded 'in_flight' (app killed mid-POST) or
 * 'dead' will never be retried, and the server may well already own that row
 * — those must still be addressed by the stripped id, per routeDelete().
 */
async function hasPendingCreate(recordId) {
  try {
    const rows = await getDB().mutationQueue
      .where('recordId')
      .equals(recordId)
      .toArray();
    return rows.some(
      (r) => r.op === 'create' && (r.status === 'pending' || r.status === 'failed')
    );
  } catch {
    return false;
  }
}

export async function drainQueue() {
  if (inFlight) return { skipped: true };
  if (isOffline()) return { offline: true };
  inFlight = true;
  try {
    let processed = 0;
    let failed = 0;
    let waiting = 0;
    // One attempt per entry per drain. listPending() returns 'failed' entries
    // first, so without this a non-network failure (400, 404 while waiting
    // for its create) would be re-picked immediately and burn all eight
    // attempts in one tight loop instead of being retried on a later drain.
    const tried = new Set();
    // Entries held back because their create has not landed. Not failures —
    // nothing is written to them, so no attempt is spent. If a create does
    // land during this drain they get one more pass immediately.
    const deferred = new Set();
    let createLanded = false;

    while (true) {
      const batch = await queue.listPending(50);
      let entry = batch.find((e) => !tried.has(e.id));
      if (!entry && createLanded && deferred.size) {
        for (const id of deferred) tried.delete(id);
        deferred.clear();
        createLanded = false;
        entry = batch.find((e) => !tried.has(e.id));
      }
      if (!entry) break;
      tried.add(entry.id);

      // If a queue entry references a local-<uuid> create that no longer
      // exists locally (e.g. user immediately deleted before push), drop it.
      if (entry.op !== 'create' && isLocalId(entry.recordId)) {
        const row = await eventsRepo.get(entry.recordId);
        if (!row) {
          await queue.dropEntry(entry.id);
          continue;
        }
        // The create is still queued for retry, so the server has never heard
        // of this row: sending the PUT/DELETE now can only 404. Hold the entry
        // back untouched rather than spending one of its eight attempts on a
        // request we know cannot succeed.
        if (await hasPendingCreate(entry.recordId)) {
          deferred.add(entry.id);
          waiting += 1;
          continue;
        }
      }

      try {
        await queue.markInFlight(entry.id);

        if (entry.entity === 'event' && entry.op === 'create') {
          const created = await rawPost(
            `/babies/${entry.babyId}/events`,
            entry.payload || {}
          );
          // POST succeeded. Beyond this point, never re-throw — the server
          // already owns the new row, and another retry would duplicate it.
          // (Build-34 hit this hard when rewriteRecordId threw on an unindexed
          // recordId column: every save produced 8 server duplicates.)
          try {
            const newId =
              created?.eventId || created?.id || created?.item?.eventId;
            if (newId && newId !== entry.recordId) {
              await queue.rewriteRecordId(entry.recordId, newId);
            } else {
              await eventsRepo.markSynced(entry.recordId);
            }
          } catch (rewriteErr) {
            // Local bookkeeping failed but the server has the row. The next
            // pull will re-fetch under the real id; drop the orphaned local
            // copy so we don't show a ghost duplicate.
            try {
              await eventsRepo.hardDelete(entry.recordId);
            } catch { /* swallow */ }
          }
          await queue.dropEntry(entry.id);
          // Anything held back waiting on a create may now be sendable.
          createLanded = true;
        } else if (entry.entity === 'event' && entry.op === 'update') {
          const targetId = serverEventId(entry.recordId);
          try {
            await rawPut(
              `/babies/${entry.babyId}/events/${targetId}`,
              entry.payload || {}
            );
          } catch (err) {
            if (isNotFound(err) && isLocalId(entry.recordId)) {
              // Waiting for create — retry after the create lands.
              throw Object.assign(err, { waitingForCreate: true });
            }
            // A 404 on a REAL id is not proof the row is gone. It used to be
            // routine: the server resolved PUT/DELETE with getEventItem(), a
            // single UNPAGINATED Query over the whole `BABY#<id>` partition,
            // and `EVENT#SLEEP#…` sorts last — so an established baby's sleeps
            // were the first rows to fall outside that 1 MB page. That lookup
            // now paginates, but a 404 is still not worth a user's edit:
            // marking the row synced and dropping
            // the entry here removed its 'local' protection, and the next
            // pull overwrote the saved wake with the server's still-open copy:
            // the sleep silently re-opened and the duration was lost. Keep the
            // row 'local' and let the entry retry instead.
            throw err;
          }
          await eventsRepo.markSynced(entry.recordId);
          await queue.dropEntry(entry.id);
        } else if (entry.entity === 'event' && entry.op === 'delete') {
          const targetId = serverEventId(entry.recordId);
          try {
            await rawDelete(`/babies/${entry.babyId}/events/${targetId}`);
          } catch (err) {
            if (isNotFound(err)) {
              if (isLocalId(entry.recordId)) {
                // Waiting for create — retry once the create has landed so
                // the server row is actually removed.
                throw Object.assign(err, { waitingForCreate: true });
              }
              // Already gone on the server — the delete is satisfied.
            } else {
              throw err;
            }
          }
          await eventsRepo.hardDelete(entry.recordId);
          await queue.dropEntry(entry.id);
        } else {
          // Unknown entry — drop so it doesn't block the queue forever.
          await queue.dropEntry(entry.id);
        }

        processed += 1;
      } catch (err) {
        if (err?.waitingForCreate) {
          // Not a failure: the create simply has not landed yet. Requeue
          // without counting an attempt so this edit can never go 'dead',
          // and keep draining the rest of the queue (breaking here would let
          // one stranded create block every other pending mutation).
          await requeuePending(entry.id);
          deferred.add(entry.id);
          waiting += 1;
          continue;
        }
        failed += 1;
        await queue.markFailed(entry.id, err);
        // If we hit a network failure mid-drain, stop — the engine will
        // retry on 'online' or after the heartbeat.
        if (isOffline() || err?.isTimeoutError || err?.isNetworkError) break;
        // Otherwise keep draining the rest (one bad row shouldn't block all).
      }
    }

    return { processed, failed, waiting };
  } finally {
    inFlight = false;
  }
}
